import { NavigationItem } from './model/navigation-item';
import type { HttpService } from '../App/services/http-service';
import type { RoutingService } from '../App/services/mx-routing-service';
import type { MxApplicationService } from '../App/services/mx-application-service';
import type { EventService } from '../App/services/event-service';
import type { StructuresService } from './structures.service';
import type { StructureNode } from './model/structure-node';
import type { UserProfileService } from '../App/services/user-profile-service';
import type { UserProfile } from '../App/model/user-profile';
import { EventCodes } from '../App/model/event-codes';
import { NavigationItemImplementation, type StructureNavigationItemImplementation } from '../Shell/PandoraSolutionBuilderAgent/model/enumerations';
import { utils } from '../Utils/mx-utils';
import { dashboardRouteName } from '../Dashboard/dashboard.module';

export type NavigationItemDTO =
	| NavigationItemSearchPageDTO
	| NavigationItemLandingPageDTO
	| NavigationItemCustomControllerDTO
	| NavigationExternalUrlDTO
	| NavigationItemDashboardDTO
	| NavigationItemDefaultDTO;

export interface NavigationItemBaseDTO {
	Id: string;
	Title: string;
	Description: string;
	Name: string;
	Application: string;
	Icon?: string;
	Disabled?: boolean;
	ShowNestedNavigationInTabs?: boolean;
}

export interface NavigationItemDefaultDTO extends NavigationItemBaseDTO {
	ImplementationType?: NavigationItemImplementation.Default;
	Children: Array<NavigationItemDTO>;
}

export interface NavigationItemSearchPageDTO extends NavigationItemBaseDTO {
	ImplementationType: NavigationItemImplementation.SearchPage;
	Widget: string;
	DataQuery: string;
	Badge?: {
		FilterId: string;
		IsNumeric?: boolean;
		Tooltip: string;
	};
	DefaultFilter?: string;
	AvailableInSearch?: boolean;

	ShowStructureInNavigation?: boolean;
	Children: Array<NavigationItemDTO | NavigationStructureDTO>;
}

export interface NavigationItemLandingPageDTO extends NavigationItemBaseDTO {
	ImplementationType: NavigationItemImplementation.LandingPage;
	Widget: string;
	Children: Array<NavigationItemDTO>;
}

export interface NavigationExternalUrlDTO extends NavigationItemBaseDTO {
	ImplementationType: NavigationItemImplementation.ExternalUrl;
	Url: string;
	OpenInNewWindow?: boolean;
	Children: Array<NavigationItemDTO>;
}

export interface NavigationItemCustomControllerDTO extends NavigationItemBaseDTO {
	ImplementationType: NavigationItemImplementation.CustomController;
	ControllerUrl: string;
	Controller: string;
	Url: string;
	Children: Array<NavigationItemDTO>;
}

export interface NavigationItemDashboardDTO extends NavigationItemBaseDTO {
	ImplementationType: NavigationItemImplementation.Dashboard;
	Children: Array<NavigationItemDTO>;
}

export interface NavigationStructureDTO {
	Widget: string;
	StructureImplementationType: StructureNavigationItemImplementation;
	StructureId: string;
	RelativePath: string;
	NodeId: string;
	Title: string;
	Name: string;
	Icon?: string;
}

export class NavigationItemsLoaderService {
	private _navReqPromise?: Promise<NavigationItemDTO[]>;
	private profile!: UserProfile;

	static $inject = [
		'mx.SolutionBuilderAgent.Http',
		'mx.shell.RoutingService',
		'mx.shell.AppsService',
		'mx.shell.EventService',
		'mx.shell.StructuresService',
		'mx.shell.UserProfileService',
	];

	constructor(
		private readonly http: HttpService,
		private readonly routingService: RoutingService,
		private readonly appsService: MxApplicationService,
		private readonly eventService: EventService,
		private readonly structuresService: StructuresService,
		private readonly userProfileService: UserProfileService
	) {}

	/**
	 * Function which returns all loaded navigation items which are related to specific DataSetWidget
	 */
	public async getNavigationItemsOfWidget(dataSetWidgetId: string): Promise<NavigationItem[]> {
		const navItems = await this.load();
		return this._getNavigationItemsOfDataSetWidget(navItems, dataSetWidgetId);
	}

	public async load(reload = false) {
		if (!this.appsService.getCurrentApplication()?.value) {
			return [];
		}

		if (!this._navReqPromise || reload) {
			this._navReqPromise = this.http.get(`api/Navigation?app=${this.appsService.getCurrentApplication().value}`);
		}

		this.profile = await this.userProfileService.get();

		return this.mapItems(await this._navReqPromise);
	}

	public loadNavigationItem(navigationItemId: string) {
		return this.http.get(`api/Navigation/Available/${navigationItemId}`);
	}

	/**
	 * Recursivly search for all navigation items related to specific DataSetWidget
	 *
	 * @param items
	 * @param dataSetWidgetId
	 * @returns {Array}
	 * @private
	 */
	private _getNavigationItemsOfDataSetWidget(items: NavigationItem[], dataSetWidgetId: string): NavigationItem[] {
		let result: NavigationItem[] = [];
		items.forEach((navItem) => {
			if (navItem.contentWidgetId === dataSetWidgetId) {
				result.push(navItem);
			}
			if (Array.isArray(navItem.children)) {
				result = result.concat(this._getNavigationItemsOfDataSetWidget(navItem.children, dataSetWidgetId));
			}
		});

		return result;
	}

	private mapItems(data: NavigationItemDTO[] = [], parent?: NavigationItem): NavigationItem[] {
		return data.map((item) => this.mapNavigationItem(item, parent)).filter((x) => x) as NavigationItem[];
	}

	private createStructureNavigationItem(structure: StructureNode, parent: NavigationItem, structureRootNavigationItem: NavigationItem) {
		const navigationItem = new NavigationItem(
			structure.name,
			// Navigation Item's `name` should be unique
			//TODO: for Service Catalog => Service | Bundle | Set | Group the names are similar. Should be fixed
			`${structure.config.structureId}_${structure.nodeId}`,
			structure.name,
			this.routingService.searchPageRoute
		);

		navigationItem.structure = structure;
		navigationItem.showStructureInNavigation = true;
		navigationItem.basedOnStructure = true;

		navigationItem.childrenDynamicLoader = this.getChildrenLoader(navigationItem, structureRootNavigationItem);

		navigationItem.implementationType = NavigationItemImplementation.SearchPage;

		navigationItem.id = `${structure.nodeId}_${parent.id}`; //TODO: use structure.nodePath
		navigationItem.icon = structure.icon || 'launch';
		navigationItem.dataQuery = structureRootNavigationItem.dataQuery;
		navigationItem.application = structureRootNavigationItem.application;
		navigationItem.contentWidgetId = structureRootNavigationItem.contentWidgetId;
		navigationItem.availableInSearch = false;

		navigationItem.parameters = {
			navigationId: structureRootNavigationItem.parameters?.navigationId as string,
			appString: this.appsService.getCurrentApplication().name,
			structures: navigationItem.id.slice(0, navigationItem.id.lastIndexOf('_')),
			filters: '',
		};

		navigationItem.parent = parent;

		if (structure.hasChildren) {
			navigationItem.children = structure.children?.map((child) => this.createStructureNavigationItem(child, navigationItem, structureRootNavigationItem));
		}

		return navigationItem;
	}

	private mapNavigationItem(item: NavigationItemDTO, parent?: NavigationItem) {
		let navigationItem: NavigationItem | undefined;

		switch (item.ImplementationType) {
			case NavigationItemImplementation.SearchPage:
				if (item.Widget) {
					navigationItem = new NavigationItem(item.Title, item.Name, item.Name, this.routingService.searchPageRoute);

					navigationItem.defaultFilter = item.DefaultFilter;
					navigationItem.dataQuery = item.DataQuery;

					navigationItem.parameters = {
						navigationId: item.Id,
						appString: this.appsService.getCurrentApplication().name,
						filters: '',
						structures: '',
					};
					navigationItem.contentWidgetId = item.Widget;
					if (item.Badge) {
						navigationItem.calculationBadge = {
							filter: item.Badge.FilterId,
							isNumeric: !!item.Badge.IsNumeric,
							title: item.Badge.Tooltip,
							value: '',
						};
					}

					const structureItem = item.Children?.find((child) => 'StructureId' in child && child.StructureImplementationType) as NavigationStructureDTO | undefined;

					navigationItem.availableInSearch = item.AvailableInSearch !== false;

					if (structureItem) {
						navigationItem.showStructureInNavigation = item.ShowStructureInNavigation || false;
						navigationItem.structure = this.structuresService.getStructure({
							config: {
								structureId: structureItem.StructureId,
								implementationType: structureItem.StructureImplementationType,
								widgetId: structureItem.Widget,
								relativePath: structureItem.RelativePath,
							},
							name: structureItem.Title || structureItem.Name,
							icon: structureItem.Icon,
							nodeId: structureItem.NodeId,
						});

						item.Children = item.Children.filter((child) => child !== structureItem);

						if (navigationItem.showStructureInNavigation) {
							navigationItem.childrenDynamicLoader = this.getChildrenLoader(navigationItem, navigationItem);
						}
					}
				}

				break;
			case NavigationItemImplementation.CustomController:
				navigationItem = new NavigationItem(
					item.Title,
					item.Name,
					item.Name,
					this.routingService.createRoutingItem({
						name: `${item.Name}_${utils.guid()}`,
						viewUrl: item.ControllerUrl,
						path: item.Url,
						controller: item.Controller,
					})
				);
				break;
			case NavigationItemImplementation.ExternalUrl:
				navigationItem = new NavigationItem(item.Title, item.Name, item.Name, item.Url);
				navigationItem.openInNewWindow = !!item.OpenInNewWindow;
				break;
			case NavigationItemImplementation.LandingPage:
				if (item.Widget) {
					navigationItem = new NavigationItem(item.Title, item.Name, item.Name, this.routingService.landingPageRoute);
					navigationItem.contentWidgetId = item.Widget;
					navigationItem.parameters = {
						navigationId: item.Id,
						appString: this.appsService.getCurrentApplication().name,
					};
				}
				break;
			case NavigationItemImplementation.Dashboard:
				navigationItem = new NavigationItem(item.Title, item.Name, item.Name, dashboardRouteName);
				navigationItem.parameters = {
					navigationId: item.Id,
					appString: this.appsService.getCurrentApplication().name,
				};
				break;
			default:
				navigationItem = new NavigationItem(item.Title, item.Name, item.Name);
				break;
		}

		if (!navigationItem) {
			console.error('Something is wrong with', item);
			return null;
		}
		navigationItem.implementationType = item.ImplementationType;

		navigationItem.id = item.Id;
		navigationItem.icon = item.Icon;
		navigationItem.disabled = !!item.Disabled;
		navigationItem.application = item.Application;
		navigationItem.showNestedNavigationInTabs = item.ShowNestedNavigationInTabs;
		navigationItem.description = (item.Description || '').replaceAll('{UserFullName}', `<strong>${this.profile.firstName} ${this.profile.lastName}</strong>`);

		navigationItem.children = this.mapItems(item.Children as NavigationItemDTO[], navigationItem);

		if (!parent) {
			navigationItem.icon = navigationItem.icon || 'launch';
		} else if (!navigationItem.parent) {
			navigationItem.parent = parent;
		}

		return navigationItem;
	}

	private getChildrenLoader(navigationItem: NavigationItem, structureRootNavigationItem: NavigationItem) {
		let dynamicChildren: NavigationItem[];

		return async () => {
			if (dynamicChildren) {
				return dynamicChildren;
			}

			dynamicChildren = ((await navigationItem.structure?.loadChildren()) || []).map((structure) =>
				this.createStructureNavigationItem(structure, navigationItem, structureRootNavigationItem)
			);

			navigationItem.children = navigationItem.children || [];
			navigationItem.children.push(...dynamicChildren);

			this._visitChildrenTree(navigationItem.children, navigationItem.level);

			this.eventService.dispatchEvent(EventCodes.NavigationChildrenLoaded, {
				children: navigationItem.children,
			});

			return dynamicChildren;
		};
	}

	private _visitChildrenTree(children: NavigationItem[], level: number) {
		children.forEach((child) => {
			child.level = level + 1;

			if (child.children && child.children.length) {
				this._visitChildrenTree(child.children, child.level);
			}
		});
	}
}
